'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { INSTALLATION_ID, INSTALLATION_SUCCESS } from '@/constants/url-params';
import { INSTALLATIONS_DATA_SOURCE } from '@/data/installations/constants';
import { installationsQueryKey } from '@/data/installations/queries';
import { useInstallations } from '@/data/installations/use-installations';
import { useProfile, useUpdateProfile } from '@/data/profile/use-profile.client';
import { useImpersonationStore } from '@/lib/stores/impersonation-store';
import { useInstallationStore } from '@/lib/stores/installation-store';

/**
 * Component responsible for synchronizing user profile preferences with client state.
 * Currently handles:
 * - Initializing the selected installation ID from the profile
 * - Setting default installation when none is selected
 * - Handling impersonation (admin override of installation)
 */
export function ProfilePreferencesSynchronizer() {
  const $ = _c(36);
  const {
    data: profile,
    isLoading: profileLoading
  } = useProfile();
  const {
    data: t0,
    isLoading: installationsLoading,
    isLoadingFromGithub: installationsLoadingFromGithub,
    isRefreshing: installationsRefreshing
  } = useInstallations();
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const installations = t1;
  const installationsSettling = installationsLoadingFromGithub || installationsRefreshing;
  const {
    selectedInstallationId,
    initializeFromServer,
    setSelectedInstallationId
  } = useInstallationStore();
  const {
    impersonatedInstallationId
  } = useImpersonationStore();
  const {
    mutate: updateProfile
  } = useUpdateProfile();
  const queryClient = useQueryClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  let t2;
  if ($[2] !== queryClient || $[3] !== searchParams || $[4] !== setSelectedInstallationId) {
    t2 = () => {
      const installationSuccess = searchParams.get(INSTALLATION_SUCCESS) === "true";
      const newInstallationId = searchParams.get(INSTALLATION_ID);
      if (installationSuccess && newInstallationId) {
        console.log("[ProfilePreferencesSynchronizer] New installation detected:", newInstallationId);
        setSelectedInstallationId(Number(newInstallationId));
        queryClient.invalidateQueries({
          queryKey: installationsQueryKey(INSTALLATIONS_DATA_SOURCE.GITHUB)
        });
        queryClient.invalidateQueries({
          queryKey: installationsQueryKey(INSTALLATIONS_DATA_SOURCE.DB)
        });
      }
    };
    $[2] = queryClient;
    $[3] = searchParams;
    $[4] = setSelectedInstallationId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== queryClient || $[7] !== router || $[8] !== searchParams || $[9] !== setSelectedInstallationId) {
    t3 = [searchParams, router, queryClient, setSelectedInstallationId];
    $[6] = queryClient;
    $[7] = router;
    $[8] = searchParams;
    $[9] = setSelectedInstallationId;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useEffect(t2, t3);
  console.log("[ProfilePreferencesSynchronizer] Initial state:", {
    profileLoading,
    installationsLoading,
    selectedInstallationId,
    impersonatedInstallationId,
    profile: profile ? {
      selectedInstallationId: profile.selectedInstallationId
    } : null,
    installationsCount: installations.length
  });
  let t4;
  let t5;
  if ($[11] !== impersonatedInstallationId || $[12] !== initializeFromServer) {
    t4 = () => {
      console.log("[ProfilePreferencesSynchronizer] Impersonation effect triggered:", {
        impersonatedInstallationId
      });
      if (impersonatedInstallationId !== null) {
        console.log("[ProfilePreferencesSynchronizer] Setting installation from impersonation:", impersonatedInstallationId);
        initializeFromServer(impersonatedInstallationId);
      }
    };
    t5 = [impersonatedInstallationId, initializeFromServer];
    $[11] = impersonatedInstallationId;
    $[12] = initializeFromServer;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  if ($[15] !== impersonatedInstallationId || $[16] !== initializeFromServer || $[17] !== installations || $[18] !== installationsLoading || $[19] !== installationsSettling || $[20] !== profile || $[21] !== profileLoading || $[22] !== selectedInstallationId || $[23] !== setSelectedInstallationId || $[24] !== updateProfile) {
    t6 = () => {
      console.log("[ProfilePreferencesSynchronizer] Sync effect triggered:", {
        impersonatedInstallationId,
        profileLoading,
        installationsLoading,
        installationsCount: installations.length,
        selectedInstallationId,
        profileSelectedInstallationId: profile?.selectedInstallationId
      });
      if (impersonatedInstallationId !== null) {
        console.log("[ProfilePreferencesSynchronizer] Skipping sync due to impersonation");
        return;
      }
      if (profileLoading || installationsLoading) {
        console.log("[ProfilePreferencesSynchronizer] Waiting for data to load:", {
          profileLoading,
          installationsLoading
        });
        return;
      }
      if (installations.length === 0) {
        if (selectedInstallationId !== null) {
          console.log("[ProfilePreferencesSynchronizer] No installations found, clearing stale selection:", selectedInstallationId);
          setSelectedInstallationId(null);
        }
        return;
      }
      if (profile?.selectedInstallationId) {
        console.log("[ProfilePreferencesSynchronizer] Checking profile selected installation:", profile.selectedInstallationId);
        const installationExists = installations.some(i => i.installationId === profile.selectedInstallationId);
        if (installationExists) {
          console.log("[ProfilePreferencesSynchronizer] Using profile selected installation");
          initializeFromServer(profile.selectedInstallationId);
          return;
        }
        console.log("[ProfilePreferencesSynchronizer] Profile selected installation no longer exists");
      }
      if (installationsSettling) {
        console.log("[ProfilePreferencesSynchronizer] Deferring selection writes until installations settle");
        return;
      }
      if (selectedInstallationId) {
        console.log("[ProfilePreferencesSynchronizer] Checking client-side selected installation:", selectedInstallationId);
        const installationExists_0 = installations.some(i_0 => i_0.installationId === selectedInstallationId);
        if (installationExists_0) {
          console.log("[ProfilePreferencesSynchronizer] Using client-side selected installation");
          updateProfile({
            selectedInstallationId
          });
          return;
        }
        console.log("[ProfilePreferencesSynchronizer] Client-side selected installation no longer exists");
      }
      if (!selectedInstallationId && installations.length > 0) {
        console.log("[ProfilePreferencesSynchronizer] Using first installation as default");
        const firstInstallation = installations[0];
        if (firstInstallation) {
          const firstInstallationId = firstInstallation.installationId;
          console.log("[ProfilePreferencesSynchronizer] Updating profile with first installation:", firstInstallationId);
          updateProfile({
            selectedInstallationId: firstInstallationId
          });
          initializeFromServer(firstInstallationId);
        }
      }
    };
    $[15] = impersonatedInstallationId;
    $[16] = initializeFromServer;
    $[17] = installations;
    $[18] = installationsLoading;
    $[19] = installationsSettling;
    $[20] = profile;
    $[21] = profileLoading;
    $[22] = selectedInstallationId;
    $[23] = setSelectedInstallationId;
    $[24] = updateProfile;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== impersonatedInstallationId || $[27] !== initializeFromServer || $[28] !== installations || $[29] !== installationsLoading || $[30] !== installationsSettling || $[31] !== profile || $[32] !== profileLoading || $[33] !== selectedInstallationId || $[34] !== updateProfile) {
    t7 = [profile, installations, profileLoading, installationsLoading, installationsSettling, selectedInstallationId, initializeFromServer, updateProfile, impersonatedInstallationId];
    $[26] = impersonatedInstallationId;
    $[27] = initializeFromServer;
    $[28] = installations;
    $[29] = installationsLoading;
    $[30] = installationsSettling;
    $[31] = profile;
    $[32] = profileLoading;
    $[33] = selectedInstallationId;
    $[34] = updateProfile;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  useEffect(t6, t7);
  return null;
}